/**
 * BarChart Component Architecture
 * =============================
 *
 * This component renders horizontal bar charts with support for:
 * - Single value charts (simple bars)
 * - Breakdown charts (grouped or stacked bars)
 * - Interactive tooltips and legends
 * - Responsive design with orientation detection
 *
 * Architecture Overview:
 * 1. Layout Calculations: Pre-computed constants for positioning bars, labels, and spacing
 * 2. Bar Configuration: Different configs for breakdown vs non-breakdown charts
 * 3. Data Transformation: Prepares data for different chart types (grouped/stacked)
 * 4. Rendering Pipeline: BarChartRenderer -> renderBar -> CustomizedLabel
 * 5. Interactive Features: Mouse tracking, tooltips, legends, and click handlers
 *
 * Key Components:
 * - BarChart: Main component with data preparation and layout
 * - BarChartRenderer: Handles chart rendering and interactions
 * - renderBar: Creates individual bar elements with labels
 * - CustomizedLabel: Renders labels with different positioning logic
 */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Bar,
  Cell,
  LabelList,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import _ from "lodash";
import { colors2 } from "oolib";

import { CountVizData, CountVizDatum, CountVizLabels } from "../types";

import { usePrepareData, SortOption } from "../utils/usePrepareData";
import { useTrackMousePosition } from "../utils/useTrackMousePosition";
import { calculateLabelWidths } from "./utils/calculateLabelWidths";
import { lerp } from "./utils/lerp";

import { _base_barchart_ui_configs } from "./_base_barchart_ui_configs";

import { CustomizedLabel } from "./comps/CustomizedLabel";
import { useLegendOrientation } from "../utils/useLegendOrientation";
import { Legend } from "../Legend";
import { VizArea, VizPanel, VizShell } from "../common/VizShell";
import { SidePanel } from "@/components/UI/SidePanel";
import styled, { css } from "styled-components";
import { useDynamicSummarizeAfterIdx } from "./utils/useDynamicSummarizeAfterIdx";
import TooltipV2 from "@/components/UI/Tooltip";
import { TooltipContent } from "../CustomTooltip";

// =============================================================================
// PERFORMANCE OPTIMIZATION TYPES
// =============================================================================
// These types support pre-computed cell data. Recharts requires Cell to be a
// direct child of Bar (uses React.Children internally), so we can't wrap it.
// Instead, we pre-compute expensive calculations and pass props directly.

/** Radius: single number or [topLeft, topRight, bottomRight, bottomLeft] */
type CellRadius = number | [number, number, number, number];

/** Pre-computed data for a single Cell (avoids recalculating during render) */
interface PreComputedCellData {
  cellKey: string;
  y: number;
  height: number;
  fill: string;
  radius: CellRadius;
  isZeroValue: boolean;
  width?: number; // Optional lerp-calculated width for minimum visibility
  tooltipLabel: string; // Identifies the segment for hover tracking
}

/** Visible segment indices for stacked charts (used for radius calculation) */
interface VisibleSegmentInfo {
  firstVisibleIdx: number;
  lastVisibleIdx: number;
}

// =============================================================================
// LABEL OPTIMIZATION TYPES
// =============================================================================
// These types support pre-computed label data to avoid recalculating expensive
// values and recreating objects on every render.

/** Pre-computed data for CustomizedLabel (avoids inline calculations in render) */
interface PreComputedLabelData {
  /** Unique key for React reconciliation */
  labelKey: string;
  /** Data index for this label */
  dataIndex: number;
  /** Pre-computed value prop - either single labels or array for breakdown */
  value: CountVizLabels | CountVizLabels[];
  /** Pre-computed groupData for grouped breakdowns (undefined otherwise) */
  groupData: CountVizLabels[] | undefined;
  /** Pre-computed TOT_HEIGHT_GROUPED_BAR_SECTION (undefined if not grouped) */
  totHeightGroupedBarSection: number | undefined;
  /** Pre-computed event handlers bound to this label's dataIndex */
  onMouseOver?: (
    e: React.MouseEvent<SVGRectElement, MouseEvent>,
    v: CountVizLabels | CountVizLabels[]
  ) => void;
  onMouseOut?: (
    e: React.MouseEvent<SVGRectElement, MouseEvent>,
    v: CountVizLabels | CountVizLabels[]
  ) => void;
  onMouseDown?: (
    e: React.MouseEvent<SVGRectElement, MouseEvent>,
    v: CountVizLabels | CountVizLabels[]
  ) => void;
}

export interface DebugControls {
  mouseEventDetectorOpacity: number;
  chartWrapperBgColor: string;
}

interface BarConfig {
  getBarRadius: (
    stackIndex: number,
    breakdownDisplayType: "grouped" | "stacked",
    totalStacks: number,
    data: CountVizData
  ) => number | [number, number, number, number];
  getDataKey: (stackIndex: number) => string;
  getStackId: (
    stackIndex: number,
    breakdownDisplayType: "grouped" | "stacked"
  ) => string | undefined;
  getCellFill: (
    colorIdx: number,
    stackIndex: number,
    dataVizColors: string[]
  ) => string;
}

interface BarConfigs {
  isBreakdown: BarConfig;
  isNotBreakdown: BarConfig;
}

// Y-axis offsets to position bars correctly (prevents bars starting at 0,0)
const CHART_OFFSET_TOP =
  _base_barchart_ui_configs.labelToBarGap +
  _base_barchart_ui_configs.labelHeight +
  (_base_barchart_ui_configs.labelToNextBarGap / 2 - 6);

const CHART_OFFSET_TOP_BREAKDOWN =
  _base_barchart_ui_configs.labelToBarGap +
  _base_barchart_ui_configs.labelHeight +
  _base_barchart_ui_configs.gapBetweenTwoLabels +
  _base_barchart_ui_configs.labelHeight + // twice for text and percent on separate lines
  (_base_barchart_ui_configs.labelToNextBarGap / 2 - 6);

const TOT_HEIGHT_SINGLE_BAR_SECTION =
  _base_barchart_ui_configs.barHeight +
  _base_barchart_ui_configs.labelToBarGap +
  _base_barchart_ui_configs.labelHeight +
  _base_barchart_ui_configs.labelToNextBarGap;

const TOT_HEIGHT_STACKED_BAR_SECTION =
  _base_barchart_ui_configs.stackedBarHeight +
  _base_barchart_ui_configs.labelToBarGap +
  _base_barchart_ui_configs.labelHeight +
  _base_barchart_ui_configs.gapBetweenTwoLabels +
  _base_barchart_ui_configs.labelHeight +
  _base_barchart_ui_configs.labelToNextBarGap;

// POSITION CALCULATION FUNCTIONS
// ============================================================================

const CALC_SINGLE_BAR_SECTION_Y_POS = (index: number) =>
  (TOT_HEIGHT_SINGLE_BAR_SECTION +
    _base_barchart_ui_configs.gapBetweenBarSections) *
  index;

const CALC_STACKED_BAR_SECTION_Y_POS = (index: number) =>
  (TOT_HEIGHT_STACKED_BAR_SECTION +
    _base_barchart_ui_configs.stackedGapBetweenBars) *
  index;

// Stable identity function for LabelList dataKey in breakdown charts
// Avoids creating inline function (ESLint: react-perf/jsx-no-new-function-as-prop)
const identityDataKey = (dataPoint: any) => dataPoint;

const CALC_TOTAL_HEIGHT_GROUPED_BAR_SECTION = (noOfBarsInGroup: number) => {
  const totalHeightOfAllBars =
    noOfBarsInGroup * _base_barchart_ui_configs.barHeight +
    (noOfBarsInGroup - 1) * _base_barchart_ui_configs.gapBetweenBarsInGroup;
  return (
    totalHeightOfAllBars +
    _base_barchart_ui_configs.labelToBarGap +
    _base_barchart_ui_configs.labelHeight +
    _base_barchart_ui_configs.gapBetweenTwoLabels +
    _base_barchart_ui_configs.labelHeight + // twice for text and percent on separate lines
    _base_barchart_ui_configs.labelToNextBarGap
  );
};

const CALC_GROUPED_BAR_SECTION_Y_POS = (
  index: number,
  barIndex: number,
  noOfBarsInGroup: number
) => {
  const TOTAL_HEIGHT_GROUPED_BAR_SECTION =
    CALC_TOTAL_HEIGHT_GROUPED_BAR_SECTION(noOfBarsInGroup);
  const startYPosOfGroup =
    (TOTAL_HEIGHT_GROUPED_BAR_SECTION +
      _base_barchart_ui_configs.gapBetweenGroups) *
    index;
  return (
    startYPosOfGroup +
    barIndex *
      (_base_barchart_ui_configs.barHeight +
        _base_barchart_ui_configs.gapBetweenBarsInGroup)
  );
};

const CALC_TOTAL_HEIGHT_OF_CHART = ({
  isBreakdown,
  breakdownDisplayType,
  data,
  showSumOfBreakdownValues,
  trimTrailingGap = false,
}) => {
  return isBreakdown && breakdownDisplayType === "grouped"
    ? CALC_TOTAL_HEIGHT_GROUPED_BAR_SECTION(
        (data[0] as CountVizDatum[]).length
      ) *
        data.length +
        _base_barchart_ui_configs.gapBetweenGroups * (data.length - 1) +
        _base_barchart_ui_configs.hoverRectStrokeWidth * 2
    : isBreakdown &&
      breakdownDisplayType === "stacked" &&
      !showSumOfBreakdownValues
    ? TOT_HEIGHT_STACKED_BAR_SECTION * data.length +
      _base_barchart_ui_configs.stackedGapBetweenBars * (data.length - 1) +
      _base_barchart_ui_configs.hoverRectStrokeWidth * 2
    : TOT_HEIGHT_SINGLE_BAR_SECTION * data.length +
      _base_barchart_ui_configs.gapBetweenBarSections * (data.length - 1) +
      _base_barchart_ui_configs.hoverRectStrokeWidth * 2 -
      // Each bar section budgets a trailing `labelToNextBarGap`; the last
      // section has no following bar, so when summarization is disabled
      // (and no summary bar fills that slot) drop the final gap to avoid
      // dead space below the last bar.
      (trimTrailingGap ? _base_barchart_ui_configs.labelToNextBarGap : 0);
};

const _debug_controls: DebugControls = {
  mouseEventDetectorOpacity: 0, // only increase this for debugging
  chartWrapperBgColor: "", // set this to some color value for debugging
};

const barConfigs: BarConfigs = {
  isBreakdown: {
    getBarRadius: (
      stackIndex: number,
      breakdownDisplayType: "grouped" | "stacked",
      totalStacks: number,
      data?: CountVizDatum[][] // optional for stacked analysis
    ): number | [number, number, number, number] => {
      const fullRadius = _base_barchart_ui_configs.barRadius;
      const noRadius = _base_barchart_ui_configs.barRadiusStacked ?? 0;

      if (breakdownDisplayType === "grouped") {
        return fullRadius;
      }

      // For stacked: dynamically determine visible segments
      let firstVisibleIdx = 0;
      let lastVisibleIdx = totalStacks - 1;

      if (Array.isArray(data) && data.length > 0) {
        // Take first non-transparent, non-zero segment index
        const sampleBar = data[0]; // any bar will do for structure
        const visibleSegments = sampleBar
          .map((seg, i) => ({
            i,
            visible:
              seg.labels.barColor !== "transparent" &&
              seg.labels.count > 0 &&
              seg.labels.tooltipLabel !== "",
          }))
          .filter((s) => s.visible)
          .map((s) => s.i);

        if (visibleSegments.length > 0) {
          firstVisibleIdx = visibleSegments[0];
          lastVisibleIdx = visibleSegments[visibleSegments.length - 1];
        }
      }

      if (stackIndex === firstVisibleIdx) {
        // leftmost visible
        return [fullRadius, 0, 0, fullRadius];
      } else if (stackIndex === lastVisibleIdx) {
        // rightmost visible
        return [0, fullRadius, fullRadius, 0];
      } else {
        return [noRadius, noRadius, noRadius, noRadius];
      }
    },
    getDataKey: (stackIndex: number) => `${stackIndex}.labels.count`,
    getStackId: (
      stackIndex: number,
      breakdownDisplayType: "grouped" | "stacked"
    ) => (breakdownDisplayType === "stacked" ? "stack" : `stack-${stackIndex}`),
    getCellFill: (
      colorIdx: number,
      stackIndex: number,
      dataVizColors: string[]
    ) => dataVizColors[(colorIdx + stackIndex) % dataVizColors.length],
  },
  isNotBreakdown: {
    getBarRadius: () => _base_barchart_ui_configs.barRadius,
    getDataKey: () => "labels.count",
    getStackId: () => undefined,
    getCellFill: (
      colorIdx: number,
      _stackIndex: number,
      dataVizColors: string[]
    ) => dataVizColors[colorIdx],
  },
};

type BreakdownDisplayType = "grouped" | "stacked";

export interface BarChartProps {
  data: CountVizData;
  valuePath: string | string[];
  tooltipLabelsMapping: { [key: string]: string }; //use if you have the luxury to define labels from outside the data object
  tooltipLabelsPath: string | string[]; //TAKES PRESIDENCE OVER THE ABOVE, IF EXISTS.use if labels can be found only inside the data object
  breakdownCategoryName?: string;
  labelPath: string;
  onClick?: (name: string) => void;
  // Body / sub-header of the datapoint detail panel. No renderDatumDetails
  // means no panel: the chart knows nothing about what a bar contains.
  renderDatumDetails?: (datum: any) => React.ReactNode;
  renderDatumSubHeader?: (datum: any) => React.ReactNode;
  valueSuffix?: string;
  showCount?: boolean;
  showPercent?: boolean;
  breakdownDisplayType?: BreakdownDisplayType;
  colorIdx?: number; //defines the starting point for the colors in the dataVizColors array. Useful if you are rendering multiple such charts on one page, and you want some variation in color
  summarizeAfterIdx?: number;
  style?: React.CSSProperties;
  maxTotalCount?: number; // Optional: fixes the max value for bar width calculation. Useful when bars are hidden but should maintain their original proportions
  preserveOrder?: boolean; // If true, skip sorting and preserve the original data order (useful for numeric range histograms)
  isDim1NumberInput?: boolean; // If true, sort breakdown primary groups by sortOrder when Dim1 is NumberInput
  sortBars?: SortOption; // Order of bars, or of segments within each breakdown group. Default: highest value first
  sortGroups?: SortOption; // Order of breakdown groups. Default: highest value first
  groupsFollowFirst?: boolean; // Breakdown: all groups take the first group's segment order. Default from _base_barchart_ui_configs.breakdownSortByLabel
  barColorByValue?: { [value: string]: string }; // Per-bar color override keyed by datum `value` (non-breakdown only). Listed bars get their mapped color; the rest are dimmed to grey. See usePrepareData.
  highlightValue?: string; // Draw a border around the bar whose datum `value` matches (non-breakdown only). Used to mark the applied filter.
}

// =============================================================================
// MEMOIZED LABEL CONTENT COMPONENT
// =============================================================================
// This component is passed to Recharts' LabelList content prop.
// It's memoized to prevent re-renders when parent re-renders with same props.
// All expensive calculations are pre-computed in generatePreComputedLabels.

interface MemoizedLabelContentProps {
  // Recharts passes these props to content component
  y?: number;
  width?: number;
  height?: number;
  index?: number;
  // Our pre-computed data
  preComputedLabels: PreComputedLabelData[];
  // Display configuration (stable refs from parent)
  breakdownDisplayType: "grouped" | "stacked";
  showPercent: boolean;
  showCount: boolean;
  showSumOfBreakdownValues: boolean;
  activeBar: string | undefined;
  highlightValue: string | undefined;
  stackIndex: number;
  totalDatasetCount: number;
  // Width calculation props for label alignment
  maxPercentWidth: number;
  maxCountWidth: number;
  // Label-overflow reporting (stable refs from parent)
  onLabelOverflowChange?: (name: string, overflowing: boolean) => void;
  onOverflowLabelHover?: (name: string | null) => void;
}

const MemoizedLabelContentInner: React.FC<MemoizedLabelContentProps> = ({
  y,
  width,
  height,
  index,
  preComputedLabels,
  breakdownDisplayType,
  showPercent,
  showCount,
  showSumOfBreakdownValues,
  activeBar,
  highlightValue,
  stackIndex,
  totalDatasetCount,
  maxPercentWidth,
  maxCountWidth,
  onLabelOverflowChange,
  onOverflowLabelHover,
}) => {
  if (index === undefined) return null;

  const labelData = preComputedLabels[index];
  if (!labelData) return null;

  return (
    <CustomizedLabel
      y={y}
      width={width}
      height={height}
      CALC_SINGLE_BAR_SECTION_Y_POS={CALC_SINGLE_BAR_SECTION_Y_POS}
      CALC_STACKED_BAR_SECTION_Y_POS={CALC_STACKED_BAR_SECTION_Y_POS}
      TOT_HEIGHT_SINGLE_BAR_SECTION={TOT_HEIGHT_SINGLE_BAR_SECTION}
      TOT_HEIGHT_STACKED_BAR_SECTION={TOT_HEIGHT_STACKED_BAR_SECTION}
      TOT_HEIGHT_GROUPED_BAR_SECTION={labelData.totHeightGroupedBarSection}
      value={labelData.value}
      groupData={labelData.groupData}
      breakdownDisplayType={breakdownDisplayType}
      showPercent={showPercent}
      showCount={showCount}
      showSumOfBreakdownValues={showSumOfBreakdownValues}
      opacity={1}
      activeBar={activeBar}
      highlightValue={highlightValue}
      _base_barchart_ui_configs={_base_barchart_ui_configs}
      _debug_controls={_debug_controls}
      stackIndex={stackIndex}
      totalDatasetCount={totalDatasetCount}
      maxPercentWidth={maxPercentWidth}
      maxCountWidth={maxCountWidth}
      onMouseOver={labelData.onMouseOver}
      onMouseOut={labelData.onMouseOut}
      onMouseDown={labelData.onMouseDown}
      onLabelOverflowChange={onLabelOverflowChange}
      onOverflowLabelHover={onOverflowLabelHover}
    />
  );
};

const MemoizedLabelContent = React.memo(MemoizedLabelContentInner);

// =============================================================================
// MEMOIZED TOOLTIP CONTENT COMPONENT
// =============================================================================
// This component handles its own mouse tracking to isolate state changes
// and prevent re-renders of the entire chart when mouse position changes.

interface MemoizedTooltipContentProps {
  // Recharts passes these props to content component
  label?: number;
  // Our props
  rawData: CountVizData;
  breakdownCategoryName?: string;
  vizWrapperRef: React.RefObject<HTMLDivElement>;
  breakdownDisplayType: "grouped" | "stacked";
  cellLayoutData?: { stackIndex: number; cells: PreComputedCellData[] }[];
  labelOverflowMapRef?: React.MutableRefObject<Record<string, boolean>>;
}

const MemoizedTooltipContentInner: React.FC<MemoizedTooltipContentProps> = ({
  label,
  rawData,
  breakdownCategoryName,
  vizWrapperRef,
  breakdownDisplayType,
  cellLayoutData,
  labelOverflowMapRef,
}) => {

  // Mouse tracking is isolated here - state changes don't propagate to parent
  const { mousePosition, isHovering } = useTrackMousePosition({
    containerRef: vizWrapperRef,
  });

  const activeHit = useMemo(() => {
    if (!mousePosition || !cellLayoutData || !vizWrapperRef.current) {
      return undefined;
    }

    const chartContainer = vizWrapperRef.current.querySelector('.recharts-wrapper');
    if (!chartContainer) return undefined;

    const chartRect = chartContainer.getBoundingClientRect();

    // Only show tooltip when mouse X is within the chart area (not the outer padding)
    if (mousePosition.x < chartRect.left || mousePosition.x > chartRect.right) {
      return undefined;
    }

    const relativeY = mousePosition.y - chartRect.top;

    // For grouped charts, extend each cell's Y bounds by half the gap between
    // bars so that moving between bars in the same group doesn't cause blink.
    const gapExtension = breakdownDisplayType === 'grouped'
      ? _base_barchart_ui_configs.gapBetweenBarsInGroup / 2
      : 0;

    // Every segment of a stacked row shares one Y band, so Y alone always
    // matches the first one. Ask the DOM which segment is under the pointer
    // instead: each <Bar> is one `.recharts-bar` layer (= stack index) holding
    // one rect per row (= row index), which indexes into cellLayoutData.
    // elementsFromPoint, not elementFromPoint — a classless <rect> (the hover
    // detector) paints above the segments and would swallow every hit.
    if (breakdownDisplayType === 'stacked') {
      const hovered = document
        .elementsFromPoint(mousePosition.x, mousePosition.y)
        .find((el) => el.classList.contains('recharts-rectangle'));
      const barLayer = hovered?.closest('.recharts-bar');
      // elementsFromPoint searches the whole page; only a rect inside this
      // chart can be one of ours.
      if (!hovered || !barLayer || !vizWrapperRef.current.contains(hovered)) {
        return undefined;
      }
      const stackIdx = Array.from(
        vizWrapperRef.current.querySelectorAll('.recharts-bar')
      ).indexOf(barLayer);
      // The row comes from Y, not from the rect's position among its siblings:
      // a zero-value segment renders no rect at all (Recharts drops a
      // Rectangle at width 0), so a layer can hold fewer rects than rows and
      // sibling order stops lining up with row order. Each row owns its own
      // Y band, so this is immune to the missing rects.
      const cells = cellLayoutData[stackIdx]?.cells ?? [];
      const rowIdx = cells.findIndex(
        (c) => relativeY >= c.y && relativeY <= c.y + c.height
      );
      const tooltipLabel = cells[rowIdx]?.tooltipLabel;
      return tooltipLabel ? { tooltipLabel, rowIndex: rowIdx } : undefined;
    }

    for (const { cells } of cellLayoutData) {
      for (let rowIndex = 0; rowIndex < cells.length; rowIndex++) {
        const cell = cells[rowIndex];
        if (cell.fill === 'transparent' || !cell.tooltipLabel) continue;
        const cellTop = cell.y - gapExtension;
        const cellBottom = cell.y + cell.height + gapExtension;
        if (relativeY >= cellTop && relativeY <= cellBottom) {
          return { tooltipLabel: cell.tooltipLabel, rowIndex };
        }
      }
    }
    return undefined;
  }, [mousePosition, cellLayoutData, vizWrapperRef, breakdownDisplayType]);

  const activeSegment = activeHit?.tooltipLabel;

  // Don't render tooltip when not hovering or when mouse is in empty space
  if (!isHovering || mousePosition === null || !activeSegment) {
    return null;
  }

  // Row resolved from the pointer above; Recharts' own `label` dataIndex
  // reports the FIRST series' row on a stacked hover, which named the wrong
  // group in the tooltip. Kept as the fallback.
  const rowIndex = activeHit?.rowIndex ?? label;
  const allSegments =
    rowIndex !== undefined
      ? (rawData[rowIndex] as CountVizDatum[])
          ?.map((item: CountVizDatum) => ({
            ...item.labels,
          }))
          .filter(
            (item) =>
              // Filter out gap segments
              !(
                item.name === "" &&
                item.barColor === "transparent" &&
                item.tooltipLabel === ""
              )
          )
      : [];

  const value = allSegments.filter((item) => item.tooltipLabel === activeSegment);

  // When this group's label is faded out in the chart, surface the full label
  // at the top of the tooltip.
  const groupName = allSegments[0]?.name;
  const headerLabel =
    groupName && labelOverflowMapRef?.current?.[groupName]
      ? groupName
      : undefined;

  return (
    <TooltipV2
      controlled
      isOpen={isHovering && mousePosition !== null}
      mousePosition={mousePosition ?? undefined}
      primaryContent={{
        layout: 'custom',
        render: (
          <TooltipContent
            type="bar"
            value={value}
            breakdownCategoryName={breakdownCategoryName}
            headerLabel={headerLabel}
          />
        ),
      }}
    />
  );
};

const MemoizedTooltipContent = React.memo(MemoizedTooltipContentInner);

// =============================================================================
// OVERFLOW-LABEL TOOLTIP (non-breakdown charts, which have no tooltip of their
// own): shows the full label while hovering a row whose label is faded out.
// Owns its hover state via a listener ref so row hovers re-render only this
// component, never the chart.
// =============================================================================
const OverflowLabelTooltip = ({
  vizWrapperRef,
  listenerRef,
}: {
  vizWrapperRef: React.RefObject<HTMLDivElement>;
  listenerRef: React.MutableRefObject<((label: string | null) => void) | undefined>;
}) => {
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    listenerRef.current = setLabel;
    return () => {
      listenerRef.current = undefined;
    };
  }, [listenerRef]);

  const { mousePosition, isHovering } = useTrackMousePosition({
    containerRef: vizWrapperRef,
  });

  if (!label || !isHovering || !mousePosition) return null;

  return (
    <TooltipV2
      controlled
      isOpen
      mousePosition={mousePosition}
      text={label}
    />
  );
};

/**
 * renderBar - Creates a single <Bar> element with its cells and labels
 *
 * PERFORMANCE NOTE:
 * Both Cell and Label rendering have been optimized with pre-computed data.
 * All expensive calculations are done ONCE in BarChartRenderer via useMemo/useCallback:
 * - preComputedCells: y-position, radius, fill, isZeroValue
 * - preComputedLabels: value, groupData, totHeightGroupedBarSection, event handlers
 *
 * @param stackIndex - Index of this bar in a breakdown group (0 for simple charts)
 * @param data - Chart data array
 * @param preComputedCells - Pre-calculated cell properties
 * @param preComputedLabels - Pre-calculated label properties (includes bound event handlers)
 * @param labelContentComponent - Memoized component for LabelList content (avoids inline function)
 * @param isBreakdown - Whether this is a grouped/stacked chart
 * @param breakdownDisplayType - "grouped" or "stacked"
 * @param showSumOfBreakdownValues - For summarized "Other" bar
 * @param showPercent/showCount - Display options for labels
 * @param activeBar - Currently hovered bar name
 * @param totalDatasetCount - Total count for percentage calculations
 */
const renderBar = ({
  stackIndex = 0,
  data,
  preComputedCells,
  labelContentComponent,
  isBreakdown,
  breakdownDisplayType,
  showSumOfBreakdownValues,
}: {
  stackIndex?: number;
  data: CountVizData;
  preComputedCells: PreComputedCellData[];
  labelContentComponent: React.ReactElement;
  isBreakdown: boolean;
  breakdownDisplayType: "grouped" | "stacked";
  showSumOfBreakdownValues: boolean;
}) => {
  const activeConfig = isBreakdown
    ? barConfigs.isBreakdown
    : barConfigs.isNotBreakdown;
  const totalStacks = isBreakdown ? (data[0] as CountVizDatum[]).length : 1;
  const currentStackId = activeConfig.getStackId(
    stackIndex,
    breakdownDisplayType
  );

  return (
    <Bar
      key={`stack-${stackIndex}`}
      radius={activeConfig.getBarRadius(
        stackIndex,
        breakdownDisplayType,
        totalStacks,
        data
      )}
      dataKey={activeConfig.getDataKey(stackIndex)}
      stackId={currentStackId}
      isAnimationActive={false} // tooltip issues causing re-renders, animation disabled temporarily
    >
      {/*
          PERFORMANCE OPTIMIZATION:
          Label rendering uses MemoizedLabelContent component passed as prop.
          - Avoids inline function in content prop (ESLint: react-perf/jsx-no-new-function-as-prop)
          - Event handlers are pre-bound in generatePreComputedLabels
          - Static props (y, width, height) come from Recharts via the content component
        */}
      {(!isBreakdown ||
        breakdownDisplayType === "grouped" ||
        stackIndex === 0) && (
        <LabelList
          dataKey={isBreakdown ? identityDataKey : "labels"}
          offset={0}
          position="bottom"
          content={labelContentComponent}
        />
      )}
      {/*
          PERFORMANCE OPTIMIZATION:
          Cell rendering uses pre-computed props from generatePreComputedCells.
          All expensive calculations (y-position, radius, fill, isZeroValue, lerp width)
          are done ONCE in BarChartRenderer via useMemo/useCallback,
          not per-cell during render.

          NOTE: Cell must be a direct child of Bar (Recharts requirement).
          Cannot use a wrapper component as Recharts uses React.Children internally.
        */}
      {preComputedCells.map((cellData: PreComputedCellData) => {
        // Only apply stroke for stacked breakdown charts (not summarized bars)
        const shouldApplyStroke =
          isBreakdown &&
          breakdownDisplayType === "stacked" &&
          !showSumOfBreakdownValues;

        return (
          <Cell
            key={cellData.cellKey}
            y={cellData.y}
            height={cellData.height}
            fill={cellData.fill}
            opacity={1}
            {...(shouldApplyStroke && {
              stroke: "#ffffff",
              strokeWidth: 1,
            })}
            // @ts-ignore - Recharts Cell accepts radius but TypeScript types don't reflect it
            radius={cellData.radius}
            {...(cellData.isZeroValue
              ? { width: _base_barchart_ui_configs.minBarWidthForZeroValues }
              : cellData.width
              ? { width: cellData.width }
              : {})}
          />
        );
      })}
    </Bar>
  );
};

const BarChartRenderer = ({
  data,
  rawData,
  isBreakdown,
  breakdownDisplayType,
  showSumOfBreakdownValues,
  showPercent,
  showCount,
  activeBar,
  setActiveBar,
  onClick,
  showColor,
  style,

  //
  // mousePosition,
  dataMaxValue,
  breakdownCategoryName,
  totalDatasetCount,
  vizWrapperRef,
  maxPercentWidth,
  maxCountWidth,
  trimTrailingGap = false,
  highlightValue,
}) => {

  // NOTE: Mouse tracking has been moved to MemoizedTooltipContent to isolate
  // state changes and prevent re-renders of the entire chart on mouse movement.
  // The tooltip is always rendered for breakdown charts; it handles its own visibility.

  // Track chart width for lerp-based width calculations using the SVG element
  const [chartWidth, setChartWidth] = useState<number>(0);
  const chartContainerRef = React.useRef<HTMLDivElement>(null);

  // Label-overflow plumbing (both refs so reports never re-render the chart):
  // - overflow map feeds the breakdown tooltip's full-label header
  // - hover listener feeds OverflowLabelTooltip on non-breakdown charts
  const labelOverflowMapRef = React.useRef<Record<string, boolean>>({});
  const overflowLabelHoverListenerRef =
    React.useRef<((label: string | null) => void) | undefined>(undefined);
  const onLabelOverflowChange = useCallback((name: string, overflowing: boolean) => {
    labelOverflowMapRef.current[name] = overflowing;
  }, []);
  const onOverflowLabelHover = useCallback((name: string | null) => {
    overflowLabelHoverListenerRef.current?.(name);
  }, []);

  useEffect(() => {
    const container = chartContainerRef?.current;
    if (!container) return;

    // Query the SVG element rendered by Recharts inside the container
    const svgElement = container.querySelector("svg");
    if (!svgElement) return;

    const updateWidth = () => {
      const newWidth = svgElement.getBoundingClientRect().width;
      // Only update state if width has meaningfully changed (avoid render loops)
      setChartWidth((prevWidth) => {
        // Use a threshold to prevent micro-changes from triggering re-renders
        if (Math.abs(prevWidth - newWidth) > 1) {
          return newWidth;
        }
        return prevWidth;
      });
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(svgElement);

    return () => resizeObserver.disconnect();
  }, []);


  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Pre-compute visible segment indices for stacked charts
  // ===========================================================================
  // This runs ONCE when data changes, not per-cell during render.
  // Previously, each cell in a stacked chart ran an O(n) loop to find visible segments.
  // Now we compute this map once and do O(1) lookups per cell.
  const visibleSegmentMap = useMemo<Map<
    number,
    VisibleSegmentInfo
  > | null>(() => {
    // Only needed for stacked breakdown charts
    if (!isBreakdown || breakdownDisplayType !== "stacked") {
      return null;
    }

    const map = new Map<number, VisibleSegmentInfo>();

    // Iterate through each bar group (row in the chart)
    (data as CountVizDatum[][]).forEach((group, dataIndex) => {
      const visibleIndices: number[] = [];

      // Find all visible segments in this group
      group.forEach((seg, segIdx) => {
        const isVisible =
          seg?.labels?.barColor !== "transparent" &&
          (typeof seg?.labels?.count === "number"
            ? seg.labels.count > 0
            : true) &&
          seg?.labels?.tooltipLabel !== "";

        if (isVisible) {
          visibleIndices.push(segIdx);
        }
      });

      // Store first and last visible indices for this group
      if (visibleIndices.length > 0) {
        map.set(dataIndex, {
          firstVisibleIdx: visibleIndices[0],
          lastVisibleIdx: visibleIndices[visibleIndices.length - 1],
        });
      }
    });

    return map;
  }, [data, isBreakdown, breakdownDisplayType]);

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Generate pre-computed cell data
  // ===========================================================================
  // This callback creates an array of PreComputedCellData for a given stackIndex.
  // All expensive calculations (y-position, radius, fill, isZeroValue) happen here
  // ONCE, not during the render map loop.
  const generatePreComputedCells = useCallback(
    (stackIndex: number): PreComputedCellData[] => {
      const fullRadius = _base_barchart_ui_configs.barRadius;
      const noRadius = _base_barchart_ui_configs.barRadiusStacked ?? 0;

      // Determine bar height based on chart type
      const cellHeight =
        isBreakdown &&
        breakdownDisplayType === "stacked" &&
        !showSumOfBreakdownValues
          ? _base_barchart_ui_configs.stackedBarHeight
          : _base_barchart_ui_configs.barHeight;

      // Number of bars in a grouped breakdown (needed for y-position calculation)
      const barsInGroup = isBreakdown ? (data[0] as CountVizDatum[]).length : 1;

      return data.map(
        (
          d: CountVizDatum | CountVizDatum[],
          index: number
        ): PreComputedCellData => {
          // ---------------------------------------------------------------------
          // 1. Calculate Y position
          // ---------------------------------------------------------------------
          let y: number;
          if (!isBreakdown || showSumOfBreakdownValues) {
            // Simple bar or summarized "Other" bar
            y = CALC_SINGLE_BAR_SECTION_Y_POS(index) + CHART_OFFSET_TOP;
          } else if (breakdownDisplayType === "stacked") {
            // Stacked breakdown
            y =
              CALC_STACKED_BAR_SECTION_Y_POS(index) +
              CHART_OFFSET_TOP_BREAKDOWN;
          } else {
            // Grouped breakdown
            y =
              CALC_GROUPED_BAR_SECTION_Y_POS(index, stackIndex, barsInGroup) +
              CHART_OFFSET_TOP_BREAKDOWN;
          }

          // ---------------------------------------------------------------------
          // 2. Calculate fill color
          // ---------------------------------------------------------------------
          const fill = showColor
            ? !isBreakdown
              ? (d as CountVizDatum)?.labels?.barColor ?? "transparent"
              : (d as CountVizDatum[])[stackIndex]?.labels?.barColor ??
                "transparent"
            : colors2.grey15;

          // ---------------------------------------------------------------------
          // 3. Calculate isZeroValue (for minimum width handling)
          // ---------------------------------------------------------------------
          const currentValue = !isBreakdown
            ? (d as CountVizDatum)?.labels?.count ?? 0
            : (d as CountVizDatum[])[stackIndex]?.labels?.count ?? 0;

          const isZeroValue =
            currentValue === 0 &&
            (!isBreakdown ||
              (isBreakdown &&
                breakdownDisplayType === "grouped" &&
                (d as CountVizDatum[])[stackIndex]?.labels?.tooltipLabel !==
                  "" &&
                (d as CountVizDatum[])[stackIndex]?.labels?.barColor !==
                  "transparent"));

          // ---------------------------------------------------------------------
          // 4. Calculate radius
          // ---------------------------------------------------------------------
          let radius: CellRadius;

          if (!isBreakdown || breakdownDisplayType === "grouped") {
            // Simple or grouped: full radius on all corners
            radius = fullRadius;
          } else if (visibleSegmentMap) {
            // Stacked: use pre-computed visible segment info for O(1) lookup
            const segmentInfo = visibleSegmentMap.get(index);

            if (!segmentInfo) {
              // No visible segments in this group
              radius = [noRadius, noRadius, noRadius, noRadius];
            } else if (
              stackIndex === segmentInfo.firstVisibleIdx &&
              stackIndex === segmentInfo.lastVisibleIdx
            ) {
              // Only one visible segment - round all corners
              radius = fullRadius;
            } else if (stackIndex === segmentInfo.firstVisibleIdx) {
              // Leftmost visible - round left corners
              radius = [fullRadius, 0, 0, fullRadius];
            } else if (stackIndex === segmentInfo.lastVisibleIdx) {
              // Rightmost visible - round right corners
              radius = [0, fullRadius, fullRadius, 0];
            } else {
              // Middle segment - no rounding
              radius = [noRadius, noRadius, noRadius, noRadius];
            }
          } else {
            // Fallback (shouldn't happen)
            radius = fullRadius;
          }

          // ---------------------------------------------------------------------
          // 5. Calculate lerp-based width for minimum visibility
          // ---------------------------------------------------------------------
          let width: number | undefined = undefined;

          // Check if this bar should have lerp-calculated width applied
          // Skip for transparent/spacing segments in stacked charts
          const shouldApplyLerpWidth =
            !isBreakdown ||
            (isBreakdown &&
              breakdownDisplayType === "grouped" &&
              (d as CountVizDatum[])[stackIndex]?.labels?.tooltipLabel !== "" &&
              (d as CountVizDatum[])[stackIndex]?.labels?.barColor !==
                "transparent");

          if (shouldApplyLerpWidth && chartWidth && dataMaxValue) {
            const percentage = (currentValue / dataMaxValue) * 100;
            const minWidth = _base_barchart_ui_configs.minBarWidthForZeroValues;
            // Account for chart margins and VizArea padding (1.6rem = 16px on each side)
            const vizAreaPadding = 16 * 2; // 1.6rem padding on left and right
            const availableWidth =
              chartWidth -
              _base_barchart_ui_configs.marginLeft -
              _base_barchart_ui_configs.marginRight -
              vizAreaPadding;

            width = lerp([0, 100], [minWidth, availableWidth], percentage);
          }

          // ---------------------------------------------------------------------
          // 6. Extract tooltipLabel for hover tracking
          // ---------------------------------------------------------------------
          const tooltipLabel = !isBreakdown
            ? (d as CountVizDatum)?.labels?.tooltipLabel ?? ''
            : (d as CountVizDatum[])[stackIndex]?.labels?.tooltipLabel ?? '';

          return {
            cellKey: `cell-${index}-${stackIndex}`,
            y,
            height: cellHeight,
            fill,
            radius,
            isZeroValue,
            width,
            tooltipLabel,
          };
        }
      );
    },
    [
      data,
      isBreakdown,
      breakdownDisplayType,
      showSumOfBreakdownValues,
      showColor,
      visibleSegmentMap,
      chartWidth,
      dataMaxValue,
    ]
  );

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Pre-compute TOT_HEIGHT_GROUPED_BAR_SECTION
  // ===========================================================================
  // This value only changes when the number of bars in a group changes.
  // Pre-computing avoids recalculating it for every label in the render loop.
  const totHeightGroupedBarSection = useMemo(() => {
    if (!isBreakdown || breakdownDisplayType !== "grouped") {
      return undefined;
    }
    return CALC_TOTAL_HEIGHT_GROUPED_BAR_SECTION(
      (data[0] as CountVizDatum[]).length
    );
  }, [isBreakdown, breakdownDisplayType, data]);

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Pre-bound event handlers per dataIndex
  // ===========================================================================
  // These are created once per data item and bound to their specific dataIndex.
  // This avoids creating new function references in the render loop.
  const boundEventHandlers = useMemo(() => {
    if (!onClick) return null;

    return data.map((d: CountVizDatum | CountVizDatum[], dataIndex: number) => {
      const getName = () =>
        isBreakdown
          ? (data[dataIndex] as CountVizDatum[])[0].labels.name
          : (d as CountVizDatum).labels.name;

      return {
        onMouseOver: (
          _e: React.MouseEvent<SVGRectElement, MouseEvent>,
          _v: CountVizLabels | CountVizLabels[]
        ) => {
          setActiveBar(getName());
        },
        onMouseOut: (
          _e: React.MouseEvent<SVGRectElement, MouseEvent>,
          _v: CountVizLabels | CountVizLabels[]
        ) => {
          setActiveBar(undefined);
        },
        onMouseDown: (
          _e: React.MouseEvent<SVGRectElement, MouseEvent>,
          v: CountVizLabels | CountVizLabels[]
        ) => {
          setActiveBar(getName());
          onClick({ ...v });
        },
      };
    });
  }, [onClick, isBreakdown, data, setActiveBar]);

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Pre-compute label data
  // ===========================================================================
  // This callback generates PreComputedLabelData[] for a given stackIndex.
  // Expensive transformations (value extraction, groupData creation) happen ONCE,
  // not during the render loop. Event handlers are pre-bound to avoid inline functions.
  const generatePreComputedLabels = useCallback(
    (stackIndex: number): PreComputedLabelData[] => {
      return data.map(
        (
          d: CountVizDatum | CountVizDatum[],
          index: number
        ): PreComputedLabelData => {
          // Compute value based on chart type
          let value: CountVizLabels | CountVizLabels[];
          if (!isBreakdown) {
            // Simple chart - just extract labels
            value = (d as CountVizDatum).labels;
          } else if (breakdownDisplayType === "grouped") {
            // Grouped breakdown - extract specific stack's labels
            value = (d as CountVizDatum[])[stackIndex].labels;
          } else {
            // Stacked breakdown - map all labels in the group
            value = (d as CountVizDatum[]).map((item: CountVizDatum) => ({
              ...item.labels,
            }));
          }

          // Compute groupData only for grouped breakdowns
          const groupData =
            isBreakdown && breakdownDisplayType === "grouped"
              ? (d as CountVizDatum[]).map((item: CountVizDatum) => item.labels)
              : undefined;

          // Get pre-bound event handlers for this dataIndex (if onClick exists)
          const handlers = boundEventHandlers?.[index];

          return {
            labelKey: `label-${index}-${stackIndex}`,
            dataIndex: index,
            value,
            groupData,
            totHeightGroupedBarSection,
            onMouseOver: handlers?.onMouseOver,
            onMouseOut: handlers?.onMouseOut,
            onMouseDown: handlers?.onMouseDown,
          };
        }
      );
    },
    [
      data,
      isBreakdown,
      breakdownDisplayType,
      totHeightGroupedBarSection,
      boundEventHandlers,
    ]
  );

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Pre-compute all label content components
  // ===========================================================================
  // Creates stable React elements for each stackIndex.
  // This avoids inline functions (ESLint: react-perf/jsx-no-new-function-as-prop)
  // and inline JSX (ESLint: react-perf/jsx-no-jsx-as-prop) in the render loop.
  const preComputedLabelComponents = useMemo(() => {
    // Use max segment count across all bars, not just first bar
    // (after filtering, different bars may have different segment counts)
    const numStacks = isBreakdown
      ? Math.max(...(data as CountVizDatum[][]).map(group => group.length))
      : 1;

    return Array.from({ length: numStacks }, (_, stackIndex) => {
      const preComputedLabels = generatePreComputedLabels(stackIndex);
      return {
        stackIndex,
        preComputedLabels,
        preComputedCells: generatePreComputedCells(stackIndex),
        labelContentComponent: (
          <MemoizedLabelContent
            preComputedLabels={preComputedLabels}
            breakdownDisplayType={breakdownDisplayType}
            showPercent={showPercent}
            showCount={showCount}
            showSumOfBreakdownValues={showSumOfBreakdownValues}
            activeBar={activeBar}
            highlightValue={highlightValue}
            stackIndex={stackIndex}
            totalDatasetCount={totalDatasetCount}
            maxPercentWidth={maxPercentWidth}
            maxCountWidth={maxCountWidth}
            onLabelOverflowChange={onLabelOverflowChange}
            onOverflowLabelHover={onOverflowLabelHover}
          />
        ),
      };
    });
  }, [
    isBreakdown,
    data,
    generatePreComputedLabels,
    generatePreComputedCells,
    breakdownDisplayType,
    showPercent,
    showCount,
    showSumOfBreakdownValues,
    activeBar,
    highlightValue,
    totalDatasetCount,
    maxPercentWidth,
    maxCountWidth,
    onLabelOverflowChange,
    onOverflowLabelHover,
  ]);

  // This allows the tooltip to determine which segment is hovered based on mouse Y position
  // without requiring state updates in the parent BarChart component.
  const cellLayoutData = useMemo(() => {
    return preComputedLabelComponents.map((computed) => ({
      stackIndex: computed.stackIndex,
      cells: computed.preComputedCells,
    }));
  }, [preComputedLabelComponents]);

  // ===========================================================================
  // PERFORMANCE OPTIMIZATION: Pre-compute tooltip content element
  // ===========================================================================
  // Creates a stable React element for the Tooltip content prop.
  // Avoids inline function (ESLint: react-perf/jsx-no-new-function-as-prop).
  // Mouse tracking is handled inside MemoizedTooltipContent to isolate state changes.
  const tooltipContentElement = useMemo(
    () => (
      <MemoizedTooltipContent
        rawData={rawData}
        breakdownCategoryName={breakdownCategoryName}
        vizWrapperRef={vizWrapperRef}
        breakdownDisplayType={breakdownDisplayType}
        cellLayoutData={cellLayoutData}
        labelOverflowMapRef={labelOverflowMapRef}
      />
    ),
    [
      rawData,
      breakdownCategoryName,
      vizWrapperRef,
      breakdownDisplayType,
      cellLayoutData,
    ]
  );

  return (
    <div ref={chartContainerRef}>
      {/* Breakdown charts surface overflowed labels in their own tooltip
          (headerLabel); non-breakdown charts get this dedicated one. */}
      {!isBreakdown && (
        <OverflowLabelTooltip
          vizWrapperRef={vizWrapperRef}
          listenerRef={overflowLabelHoverListenerRef}
        />
      )}
      <ResponsiveContainer
        width="100%"
        height={CALC_TOTAL_HEIGHT_OF_CHART({
          isBreakdown,
          breakdownDisplayType,
          showSumOfBreakdownValues,
          data,
          trimTrailingGap,
        })}
      >
        <RechartsBarChart
          data={data}
          layout="vertical"
          style={{ ...style }}
          margin={{
            top: 0,
            right: _base_barchart_ui_configs.marginRight,
            left: _base_barchart_ui_configs.marginLeft,
            bottom: 0,
          }}
        >
          <XAxis hide type="number" domain={[0, dataMaxValue]} />
          <YAxis hide type="category" />
          {/* Tooltip is rendered for breakdown charts; mouse tracking & visibility handled internally */}
          {isBreakdown && (
            <Tooltip
              cursor={false}
              content={tooltipContentElement}
              allowEscapeViewBox={{ x: true, y: true }}
            />
          )}
          {isBreakdown
            ? preComputedLabelComponents.map((computed) =>
                renderBar({
                  stackIndex: computed.stackIndex,
                  data,
                  preComputedCells: computed.preComputedCells,
                  labelContentComponent: computed.labelContentComponent,
                  isBreakdown,
                  breakdownDisplayType,
                  showSumOfBreakdownValues,
                })
              )
            : renderBar({
                stackIndex: 0,
                data,
                preComputedCells:
                  preComputedLabelComponents[0].preComputedCells,
                labelContentComponent:
                  preComputedLabelComponents[0].labelContentComponent,
                isBreakdown,
                breakdownDisplayType,
                showSumOfBreakdownValues,
              })}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};

// A bar keeps its bucket id in `value`; a breakdown segment its row's id in
// `rowValue` and its own in `breakdownValue`. The name only stands in for data
// without ids.
const datumKey = (labels: any) =>
  `${labels.rowValue ?? labels.value ?? labels.name}|${labels.breakdownValue ?? ""}`;

// The selected datum's current labels (bars, segments or summarized Others),
// or null once it's gone.
const findDatum = (sets: any[], key: string | null) => {
  if (!key) return null;
  for (const d of sets.flat(2)) {
    if (d?.labels && datumKey(d.labels) === key) return d.labels;
  }
  return null;
};

export const BarChart: React.FunctionComponent<BarChartProps> = ({
  data: _data,
  valuePath,
  style = {},
  tooltipLabelsMapping,
  tooltipLabelsPath,
  breakdownCategoryName, // basically, if lets say tagCat1 is broken down by tagCat2. then tagCat2's display would be this property
  labelPath = "name",
  onClick,
  renderDatumDetails,
  renderDatumSubHeader,
  breakdownDisplayType = "grouped",
  showCount = true,
  showPercent = true,
  colorIdx = 0,
  summarizeAfterIdx, // if a number is defined, then from that 'index' onwards, bars will be compressed into a single bar with a tooltip explaining distribution. If undefined, auto-calculates based on container height (non-breakdown only)
  maxTotalCount, // when provided, fixes the max value for bar width calculation (bars maintain original proportions when hidden)
  preserveOrder = false, // when true, skip sorting and preserve original data order (for numeric range histograms)
  isDim1NumberInput = false, // when true, sort breakdown primary groups by sortOrder when Dim1 is NumberInput
  sortBars,
  sortGroups,
  groupsFollowFirst = _base_barchart_ui_configs.breakdownSortByLabel,
  barColorByValue,
  highlightValue,
}) => {

  const isBreakdown = Array.isArray(valuePath);

  const autoFitContainerRef = React.useRef<HTMLDivElement>(null);

  // Auto-fit bar count: only enabled for non-breakdown charts when summarizeAfterIdx is undefined
  const shouldAutoFit = !isBreakdown && summarizeAfterIdx === undefined;

  const { calculatedSummarizeAfterIdx } = useDynamicSummarizeAfterIdx({
    containerRef: autoFitContainerRef,
    totalDataCount: _data?.length ?? 0,
    enabled: shouldAutoFit,
  });

  // Determine the effective summarizeAfterIdx:
  // - If explicitly provided, use that (takes precedence)
  // - If auto-fit is enabled and calculated, use calculated value
  // - Otherwise, undefined (no summarization)
  const effectiveSummarizeAfterIdx = summarizeAfterIdx ?? calculatedSummarizeAfterIdx;

  const {
    data: rawData,
    dataMaxValue: calculatedDataMaxValue,
    dataToSummarize,
    isTotalSummarizedCountHigherThanMax,
  } = usePrepareData({
    _data,
    labelPath,
    valuePath,
    tooltipLabelsMapping,
    tooltipLabelsPath,
    isBreakdown,
    showPercent,
    summarizeAfterIdx: effectiveSummarizeAfterIdx,
    colorIdx,
    breakdownDisplayType,
    sortBars,
    sortGroups,
    groupsFollowFirst,
    preserveOrder,
    isDim1NumberInput,
    barColorByValue,
  });

  // Use maxTotalCount when provided (keeps bar widths stable when bars are hidden)
  const dataMaxValue = maxTotalCount ?? calculatedDataMaxValue;

  const data = useMemo(() => {
    if (!isBreakdown || breakdownDisplayType !== "stacked") {
      return rawData;
    }

    // Filter out zero-value segments in stacked charts (but keep them in grouped charts)
    return rawData.map((group: CountVizDatum[]) => {
      return group.filter(
        (segment) =>
          segment.labels.barColor !== "transparent" && segment.labels.count > 0
      );
    });
  }, [rawData, isBreakdown, breakdownDisplayType]);

  const { vizWrapperRef, orientation, shouldShowLegend } = useLegendOrientation(
    {
      widthThreshold: 400,
      chartType: "bar",
      displayType: breakdownDisplayType,
      legendItems: isBreakdown
        ? (data[0] as CountVizDatum[]).filter(
            (item) =>
              !(
                item.labels.name === "" &&
                item.labels.barColor === "transparent" &&
                item.labels.tooltipLabel === ""
              )
          )
        : [],
    }
  );

  const [activeBar, setActiveBar] = useState<string | undefined>(undefined);

  // Only the key is stored; the labels are read from the current data, so the
  // panel never shows a bar that has since moved or gone.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selectedDatum = useMemo(
    () => findDatum([rawData, dataToSummarize ?? []], selectedKey),
    [rawData, dataToSummarize, selectedKey]
  );
  useEffect(() => {
    if (selectedKey && !selectedDatum) setSelectedKey(null);
  }, [selectedKey, selectedDatum]);

  // A stacked bar hands over the whole stack, which pins no single bucket.
  const handleDatumClick = useMemo(() => {
    if (!onClick && !renderDatumDetails) return undefined;
    return (v: any) => {
      onClick?.(v);
      if (renderDatumDetails && v && !Array.isArray(v)) setSelectedKey(datumKey(v));
    };
  }, [onClick, renderDatumDetails]);

  // Selected reuses the marks the chart already has: the persistent grey80
  // outline for a bar with an id, the row outline for a breakdown segment
  // (whose own id no mark matches). Hover still wins while it lasts.
  const effectiveHighlightValue = selectedDatum?.value ?? highlightValue;
  const effectiveActiveBar = activeBar ?? (selectedDatum?.name || undefined);

  const totalDatasetCount = useMemo(() => {
    if (!isBreakdown) {
      return data.reduce(
        (sum, item) => sum + (item as CountVizDatum).labels.count,
        0
      );
    } else {
      return data.reduce((sum, group) => {
        if (Array.isArray(group)) {
          return (
            sum +
            group.reduce((groupSum, item) => groupSum + item.labels.count, 0)
          );
        }
        return sum;
      }, 0);
    }
  }, [data, isBreakdown]);

  const { maxPercentWidth, maxCountWidth } = useMemo(() => {
    return calculateLabelWidths({
      data: rawData,
      isBreakdown,
      breakdownDisplayType,
      showPercent,
      showCount,
      totalDatasetCount,
    });
  }, [
    rawData,
    isBreakdown,
    breakdownDisplayType,
    showPercent,
    showCount,
    totalDatasetCount,
  ]);

  if (data.length === 0) return <div>No data available</div>;

  // Rows are sparse: each holds only the categories present in it, addressed
  // positionally, so no single row lists every category. The legend is the
  // union across all rows — the same set the colour mapping was built from,
  // which is why colours already agree from row to row — deduped by label in
  // first-seen order.
  const getLegendData = () => {
    if (!data?.length || breakdownDisplayType !== "stacked") return [];

    const seen = new Set<string>();
    const legend: { text: string; color: string }[] = [];
    for (const row of data as CountVizDatum[][]) {
      if (!Array.isArray(row)) continue;
      for (const d of row) {
        const text = d.labels.tooltipLabel;
        const color = d.labels.barColor;
        if (text === "" && color === "transparent") continue; // spacer segment
        if (seen.has(text)) continue;
        seen.add(text);
        legend.push({ text, color });
      }
    }
    return legend;
  };

  return (
    <div
      ref={autoFitContainerRef}
      className="barChartAutoFitContainer"
      style={{
        width: "100%",
        height: "100%",
        minHeight: 0, // Allow flex shrinking
      }}
    >
    <div
      className="barChartCompWrapper"
      ref={vizWrapperRef}
      style={{
        width: "100%",
        height: "100%",
        background: _debug_controls.chartWrapperBgColor,
        display: "flex",
        gap: "8px",
        flexDirection: orientation === "vertical" ? "row" : "column",
        position: "relative",
        overflow: "auto",
        ...style,
      }}
    >
      <VizShell orientation={orientation}>
        <VizArea style={{ paddingTop: "0" }}>
          <BarChartRenderer
            {...{
              data,
              rawData,
              showColor: true,
              dataToSummarize,
              isBreakdown,
              breakdownDisplayType,
              showSumOfBreakdownValues: false,
              showPercent,
              showCount,
              activeBar: effectiveActiveBar,
              setActiveBar,
              onClick: handleDatumClick,
              // mousePosition: isHovering ? mousePosition : null,
              dataMaxValue,
              breakdownCategoryName,
              totalDatasetCount,
              vizWrapperRef,
              style: {},
              maxPercentWidth,
              maxCountWidth,
              // No summary bar fills the last section's trailing gap → trim it
              // so there's no dead space below the final bar.
              trimTrailingGap: !isBreakdown && !dataToSummarize,
              highlightValue: effectiveHighlightValue,
            }}
          />
          {dataToSummarize && (
            <StyledBarChartMask
              showFadeAtTheEnd={isTotalSummarizedCountHigherThanMax}
            >
              <BarChartRenderer
                {...{
                  data: dataToSummarize,
                  rawData: dataToSummarize,
                  isBreakdown: true,
                  breakdownDisplayType: "stacked",
                  showSumOfBreakdownValues: true,
                  showPercent,
                  showColor: false,
                  showCount,
                  activeBar: effectiveActiveBar,
                  setActiveBar,
                  onClick: handleDatumClick,
                  // mousePosition: isHovering ? mousePosition : null,
                  dataMaxValue,
                  breakdownCategoryName,
                  totalDatasetCount,
                  vizWrapperRef,
                  style: {
                    top: `-${_base_barchart_ui_configs?.gapBetweenSummarizedBars}rem`,
                  },
                  maxPercentWidth,
                  maxCountWidth,
                  // Last thing in the chart: no bar follows, so no trailing gap.
                  trimTrailingGap: true,
                }}
              />
            </StyledBarChartMask>
          )}
        </VizArea>

        {shouldShowLegend && breakdownDisplayType === "stacked" && (
          <VizPanel
            style={style}
            position={orientation === "vertical" ? "right" : "bottom"}
          >
            <Legend
              orientation={orientation}
              label={breakdownCategoryName}
              data={getLegendData()}
            />
          </VizPanel>
        )}
      </VizShell>
      <SidePanel
        isOpen={!!selectedDatum}
        onClose={() => setSelectedKey(null)}
        title={
          selectedDatum &&
          // A summarized "Others" bar is its real bucket under the group label.
          (selectedDatum.name === "Others"
            ? selectedDatum.tooltipLabel
            : selectedDatum.tooltipLabel && selectedDatum.tooltipLabel !== selectedDatum.name
              ? `${selectedDatum.name} \u00D7 ${selectedDatum.tooltipLabel}`
              : selectedDatum.name)
        }
        subHeader={selectedDatum && renderDatumSubHeader?.(selectedDatum)}
        closeOnOutsideClick={false}
        exclusiveGroup="datumDetail"
        contentKey={selectedKey}
      >
        {selectedDatum && renderDatumDetails(selectedDatum)}
      </SidePanel>
    </div>
    </div>
  );
};

const StyledBarChartMask = styled.div`
  position: relative;
  /* overflow: hidden;  */
  ${({ showFadeAtTheEnd }) =>
    showFadeAtTheEnd &&
    css`
      &:after {
        content: "";
        position: absolute;
        top: -${_base_barchart_ui_configs?.gapBetweenSummarizedBars}rem;
        right: 0;
        width: 10rem;
        height: 100%;
        pointer-events: none;
        background: linear-gradient(
          to right,
          rgba(255, 255, 255, 0) 0%,
          #fff 100%
        );
      }
    `}
`;

/**
 * BarChart Working Explanation
 * ===========================
 *
 * 1. DATA FLOW:
 *    Raw data → usePrepareData → Transform for stacked charts → Render
 *
 * 2. CHART TYPES:
 *    - Single: One value per bar (valuePath = string)
 *    - Grouped: Multiple bars per category (valuePath = string[])
 *    - Stacked: Segments stacked in one bar (valuePath = string[])
 *
 * 3. RENDERING PIPELINE:
 *    BarChart → BarChartRenderer → renderBar → CustomizedLabel
 *
 * 4. POSITIONING:
 *    - CALC_*_Y_POS functions calculate bar positions
 *    - CHART_OFFSET_TOP adds spacing from chart top
 *    - TOT_HEIGHT_* constants define section heights
 *
 * 5. INTERACTIONS:
 *    - Mouse tracking for tooltips
 *    - Active bar highlighting
 *    - Click handlers for data selection
 *
 * 6. LAYOUT:
 *    - Responsive container with orientation detection
 *    - Legend panel for stacked charts
 *    - Hover rectangles for interaction zones
 */
